<template>
    <ais-instant-search
        :search-client="searchClient"
        :index-name="indexName"
        :search-function="searchFunction"
        :initial-ui-state="initialUiState"
    >
        <ais-configure
            :hits-per-page.camel="hitPerPage"
            :around-lat-lng.camel="algoliaCoordsFormat"
            :around-radius.camel="viewableRadius"
            :filters="presetFacets"
        />

        <transition name="fade">
            <div
                v-if="filters && isMobile"
                class="fixed inset-0 z-50 bg-black bg-opacity-50"
                @click="closeFilters"
            ></div>
        </transition>

        <transition name="slide">
            <Drawer v-show="filters && isMobile" @close="closeFilters" />
        </transition>

        <FilterBar
            :view.sync="view"
            :filters="filters"
            :categories="categories"
            :default-category="defaultExtrasCategory"
            :hide-search-bar-ui="hideSearchBarUi"
            :hide-categories-ui="hideCategoriesUi"
            :hide-filters-ui="hideFiltersUi"
            :view-swappable="viewSwappable"
            :map-info="mapInfoForMap"
            @searched="newSearch"
            @open="openFilters"
            @close="closeFilters"
        />

        <div>
            <ListView
                v-if="view === 'list'"
                ref="list"
                class="mx-auto sm:max-w-lg sm:py-20"
                :map-info="mapInfoForList"
                @report-map="setMapInfoForList"
            />
            <MapView
                v-if="view === 'map'"
                ref="map"
                :map-defaults="mapDefaults"
                :use-geo-location="useGeoLocation"
                :minimal-ui="minimalUi"
                :list-heading="extrasListHeading"
                :hide-website-in-listings="hideWebsiteInListings"
                @report-map="setMapInfo"
            />
        </div>
    </ais-instant-search>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import ListView from './ListView.vue';
import FilterBar from './FilterBar.vue';
import MapView from './MapView.vue';
import Drawer from './Drawer.vue';

export default {
    components: {
        FilterBar,
        MapView,
        Drawer,
        ListView
    },

    props: {
        defaultExtrasCategory: {
            type: String,
            default: undefined
        },
        defaultFiltersPayments: {
            type: Array,
            default() {
                return [];
            }
        },
        defaultFiltersServices: {
            type: Array,
            default() {
                return [];
            }
        },
        minimalUi: {
            type: Boolean,
            default: false
        },
        hideSearchBarUi: {
            type: Boolean,
            default: false
        },
        hideFiltersUi: {
            type: Boolean,
            default: false
        },
        hideCategoriesUi: {
            type: Boolean,
            default: false
        },
        viewSwappable: {
            type: Boolean,
            default: false
        },
        defaultToListView: {
            type: Boolean,
            default: false
        },
        coordinates: {
            type: Object,
            default() {
                return {
                    lat: -34.9285,
                    lng: 138.6007
                };
            }
        },
        useGeoLocation: {
            type: Boolean,
            default: true
        },
        zoom: {
            type: Number,
            default: 11
        },
        hideWebsiteInListings: {
            type: Boolean,
            default: false
        },
        extrasListHeading: {
            type: String,
            default: ''
        },
        healthPartnersProvidersOnly: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            view: 'map',
            indexName: 'providers',
            searchClient: algoliasearch(window.algoliaApplicationId, window.algoliaSearchApiKey),
            filters: false,
            mapInfoForMap: {},
            mapInfoForList: null,
            categories: [],
            viewableRadius: 'all',
            initialUiState: {
                providers: {
                    menu: {
                        'categories.lvl0': this.defaultExtrasCategory
                    },
                    refinementList: {
                        payments: this.defaultFiltersPayments.length
                            ? this.defaultFiltersPayments
                            : undefined,
                        services: this.defaultFiltersServices.length
                            ? this.defaultFiltersServices
                            : undefined
                    }
                }
            }
        };
    },

    computed: {
        hitPerPage() {
            return this.view === 'map' ? 999 : 10;
        },

        isMobile() {
            return this.$mq === 'mobile';
        },

        // algoliaCoordsFormat() {
        //     return this.mapInfoForList && this.view === 'list'
        //         ? `${this.mapInfoForList.center.lat}, ${this.mapInfoForList.center.lng}`
        //         : undefined;
        // }

        algoliaCoordsFormat() {
            if (!this.mapInfoForMap.bounds || (!this.mapInfoForList && this.view === 'list'))
                return undefined;

            const center = this.mapInfoForMap.center;
            return `${center.lat}, ${center.lng}`;
        },

        mapDefaults() {
            return {
                zoom: this.zoom,
                lat: this.coordinates.lat,
                lng: this.coordinates.lng
            };
        },

        presetFacets() {
            return this.healthPartnersProvidersOnly ? `isHealthPartnersProvider:true` : undefined;
        }
    },

    watch: {
        view: {
            handler(value) {
                if (value === 'list') {
                    this.mapInfoForList = this.mapInfoForMap;
                }
            },
            immediate: true
        }
    },

    created() {
        if (this.defaultToListView) {
            this.view = 'list';
        }
        this.searchClient
            .searchForFacetValues([
                {
                    indexName: this.indexName,
                    params: {
                        facetName: 'categories.lvl0',
                        facetQuery: '',
                        maxFacetHits: 10
                    }
                }
            ])
            .then(([{ facetHits }]) => {
                this.categories.push(
                    ...facetHits.map((facet) => ({
                        label: facet.value,
                        value: facet.value,
                        isRefined: false
                    }))
                );
            });
    },

    methods: {
        newSearch(geometry) {
            this.$refs.map.activeMarker = null;
            this.$refs.map.adjustMap(geometry);
        },

        setMapInfo(info) {
            this.mapInfoForMap = info;
            this.setViewableRadius();
        },

        setMapInfoForList() {
            this.mapInfoForList = null;
        },

        setViewableRadius() {
            const center = this.mapInfoForMap.center;
            const boundsNorthEast = this.mapInfoForMap.boundsNorthEast;
            const radius = window.google.maps.geometry.spherical.computeDistanceBetween(
                center,
                boundsNorthEast
            );
            this.viewableRadius = parseInt(radius);
        },

        searchFunction(helper) {
            const refinements =
                helper.state.hierarchicalFacetsRefinements['categories.lvl0'].length +
                helper.state.disjunctiveFacetsRefinements['payments'].length +
                helper.state.disjunctiveFacetsRefinements['services'].length;

            if (refinements && this.$refs.map) this.$refs.map.showSidebar();

            helper.search();
        },

        openFilters() {
            this.filters = true;
            document.body.classList.add('overlay-active');
        },

        closeFilters() {
            this.filters = false;
            document.body.classList.remove('overlay-active');
        }
    }
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active,
.fade-enter-active,
.fade-leave-active {
    will-change: transform;
    transition: all 0.5s;
}

.slide-enter,
.slide-leave-to {
    transform: translateX(-100%);
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
